import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { HelpScope } from "@fysaro/shared";
import { resolveHelpArticleVideoEmbedUrl } from "@fysaro/shared";
import { useTheme } from "@/app/providers/ThemeProvider";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/design-system/components/ui/sheet";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import type { ConsumerHelpArticleDto } from "../api/getHelpArticleTreeByScope";
import { useHelpDrawer } from "../hooks/useHelpDrawer";
import { useGetHelpArticleTreeByScope } from "../hooks/useGetHelpArticleTreeByScope";
import { SURFACE_STYLES } from "@/design-system/lib/surface-styles";
import { cn } from "@/design-system/utils";

export function HelpPanel({ contextScope }: { contextScope?: HelpScope }) {
  const { isOpen, closeHelp, scope, activeArticleId } = useHelpDrawer();
  const { mode } = useTheme();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { t, i18n } = useTranslation("help-articles");
  const [selectedArticle, setSelectedArticle] =
    useState<ConsumerHelpArticleDto | null>(null);
  const [search, setSearch] = useState("");

  const { data: categories = [], isLoading } = useGetHelpArticleTreeByScope(
    { scope, language: i18n.language || "es" },
    { enabled: isOpen },
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedArticle(null);
      setSearch("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (contextScope && scope !== contextScope) closeHelp();
  }, [closeHelp, contextScope, scope]);

  useEffect(() => {
    if (!activeArticleId) return;
    const article = categories
      .flatMap((category) => category.articles)
      .find((item) => item.id === activeArticleId);
    if (article) setSelectedArticle(article);
  }, [activeArticleId, categories]);

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    if (!term) return categories;

    return categories
      .map((category) => ({
        ...category,
        articles: category.articles.filter((article) =>
          [article.title, article.content, ...article.keywords]
            .join(" ")
            .toLocaleLowerCase()
            .includes(term),
        ),
      }))
      .filter(
        (category) =>
          category.articles.length > 0 ||
          category.name.toLocaleLowerCase().includes(term),
      );
  }, [categories, search]);

  const selectedVideoEmbedUrl = useMemo(() => {
    if (!selectedArticle) return null;

    return resolveHelpArticleVideoEmbedUrl(selectedArticle, mode);
  }, [mode, selectedArticle]);

  if (!isOpen) return null;

  const panelContent = (
    <div className="flex h-full flex-col bg-card">
      {selectedArticle ? (
        <div className={cn("flex h-16 items-center justify-between bg-muted/20 p-4", SURFACE_STYLES.helpPanelHeaderBorder)}>
          <button
            type="button"
            onClick={() => setSelectedArticle(null)}
            className="text-xs font-semibold text-primary hover:underline"
          >
            {t("panel.back")}
          </button>
          <button
            type="button"
            onClick={closeHelp}
            aria-label={t("panel.close")}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X size={18} />
          </button>
        </div>
      ) : (
        <>
          <div className={cn("flex h-16 items-center justify-between bg-muted/20 p-4", SURFACE_STYLES.helpPanelHeaderBorder)}>
            <h3 className="text-base font-semibold">{t("panel.title")}</h3>
            <button
              type="button"
              onClick={closeHelp}
              aria-label={t("panel.close")}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
            >
              <X size={18} />
            </button>
          </div>

          <div className={cn("p-4", SURFACE_STYLES.helpPanelHeaderBorder)}>
            <label className="sr-only" htmlFor="help-search">
              {t("panel.search_label")}
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={16}
              />
              <input
                id="help-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("panel.search_placeholder")}
                className="w-full rounded-md border border-border/50 bg-background py-1.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
        </>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {selectedArticle ? (
          <article className="space-y-4">
            <h2 className="text-lg font-bold text-foreground">{selectedArticle.title}</h2>
            {selectedVideoEmbedUrl ? (
              <div className="aspect-video w-full overflow-hidden rounded-md bg-black/10">
                <iframe
                  key={`${selectedArticle.id}-${mode}`}
                  src={selectedVideoEmbedUrl}
                  title={t("panel.video", { title: selectedArticle.title })}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : null}
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {selectedArticle.content}
            </div>
          </article>
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">{t("panel.loading")}</p>
        ) : filteredCategories.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("panel.empty")}</p>
        ) : (
          <div className="space-y-4">
            {filteredCategories.map((category) => (
              <section key={category.id} className="space-y-2">
                <h4 className="text-sm font-semibold text-primary">{category.name}</h4>
                <ul className="space-y-1 pl-2">
                  {category.articles.map((article) => (
                    <li key={article.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedArticle(article)}
                        className="py-1 text-left text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {article.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!isDesktop) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && closeHelp()}>
        <SheetContent side="right" className="z-50 flex w-full flex-col p-0 sm:max-w-md [&>button]:hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>{t("panel.title")}</SheetTitle>
          </SheetHeader>
          {panelContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className={cn("sticky z-30 flex h-[calc(100vh-4rem)] w-80 shrink-0 flex-col bg-card lg:w-96", SURFACE_STYLES.helpPanelBorder)}>
      {panelContent}
    </aside>
  );
}
